import {Button} from '@dropbox/dig-components/buttons';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {useMutation} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import {snackbarAtom} from 'atoms/snackbar';
import {Layout} from 'components/DSYS/Layout';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {t} from 'i18next';
import {useAtomValue, useSetAtom} from 'jotai';
import {useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import {useNavigate} from 'react-router';
import {getBackendUrl} from 'utilities';
import {queryClient} from 'views/QueryClientWrapper';

import styles from './CompanyOSProView.module.css';

const claimCompanyOSProBadge = async () => {
  const response = await fetch(`${getBackendUrl()}/api/v1/badges/company-os-pro/claim`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    let detail = t('company_os_pro_claim_failed');
    try {
      const payload = (await response.json()) as {detail?: string};
      if (payload.detail) {
        detail = payload.detail;
      }
    } catch {
      // Fall back to a generic message if the server does not return JSON.
    }
    throw new Error(detail);
  }

  return response.json();
};

const CONFETTI_HIDE_MS = 4200;
const CONFETTI_COLORS = ['#0061FE', '#1A1918', '#FFFFFF'];
const CONFETTI_VARIANTS = [
  'driftRightWide',
  'driftRightNarrow',
  'driftLeftWide',
  'driftLeftNarrow',
] as const;
const CONFETTI_PIECES = Array.from({length: 28}, (_, index) => ({
  color: CONFETTI_COLORS[index % CONFETTI_COLORS.length],
  isLogo: index % 6 === 0,
  left: `${4 + ((index * 13) % 92)}vw`,
  variant: CONFETTI_VARIANTS[index % CONFETTI_VARIANTS.length],
}));

const CompanyOSProConfetti = ({onDone}: {onDone: () => void}) => {
  useEffect(() => {
    const hideTimerId = window.setTimeout(() => {
      onDone();
    }, CONFETTI_HIDE_MS);

    return () => {
      window.clearTimeout(hideTimerId);
    };
  }, [onDone]);

  if (typeof document === 'undefined') {
    return null;
  }

  const portalRoot = document.getElementById('main') ?? document.body;

  return createPortal(
    <div data-testid="companyos-pro-confetti" className={styles.confettiOverlay}>
      {CONFETTI_PIECES.map((piece, index) => (
        <span
          key={index}
          className={[
            styles.confettiPiece,
            styles[piece.variant],
            piece.isLogo ? styles.logoPiece : '',
          ].join(' ')}
          style={{
            animationDelay: `${index * 80}ms`,
            backgroundColor: piece.color,
            left: piece.left,
          }}
        />
      ))}
    </div>,
    portalRoot
  );
};

export const CompanyOSProView = () => {
  const navigate = useNavigate();
  const pulseUser = useAtomValue(pulseUserAtom);
  const setSnackbarMessage = useSetAtom(snackbarAtom);
  const [showConfetti, setShowConfetti] = useState(true);
  const isAuthReady = pulseUser !== undefined;
  const {mutate: claimBadge, isPending} = useMutation({
    mutationFn: claimCompanyOSProBadge,
    onSuccess: async () => {
      if (pulseUser?.ldap) {
        await queryClient.invalidateQueries({queryKey: ['badges', pulseUser.ldap]});
        navigate(ROUTE_PATHS.PROFILE.replace(':userid', pulseUser.ldap), {replace: true});
        return;
      }

      navigate(ROUTE_PATHS.HOME, {replace: true});
    },
    onError: (error: unknown) => {
      setSnackbarMessage({
        text:
          error instanceof Error && error.message
            ? error.message
            : t('company_os_pro_claim_failed'),
      });
    },
  });

  useDocumentTitle(t('company_os_pro_page_title'));

  if (!isAuthReady) {
    return null;
  }

  return (
    <Layout.Container>
      {showConfetti && <CompanyOSProConfetti onDone={() => setShowConfetti(false)} />}
      <Box
        borderColor="Border Subtle"
        borderWidth="1"
        borderStyle="Solid"
        borderRadius="Large"
        padding="32"
        marginTop="32"
      >
        <Stack gap="16">
          <Text color="subtle" isBold>
            {t('company_os_pro_eyebrow')}
          </Text>
          <Title size="large">{t('company_os_pro_heading')}</Title>
          <Text size="medium">{t('company_os_pro_body')}</Text>
          <Box>
            <Button variant="primary" onClick={() => claimBadge()} isLoading={isPending}>
              {t('company_os_pro_claim_cta')}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Layout.Container>
  );
};
